import { toast } from 'react-hot-toast';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { Button } from '@/shared/components/ui/Button';
import { useTodayLog, useAttendanceMutations } from '@/features/attendance/hooks/useAttendance';

export const timeSummaryConfig: WidgetConfig = {
  id: 'dash-time-summary',
  title: 'Time Summary',
  category: 'utility',
  refreshStrategy: 'manual',
  defaultSize: { w: 4, h: 4 },
  priority: 6,
};

/**
 * Real attendance summary — backed by GET /api/time-logs/today.
 * Status and hours are derived from the persisted TimeLog; no mock fallback.
 */
export function TimeSummaryWidget() {
  const { data, isLoading, isError, error, refetch } = useTodayLog();
  const { checkIn, checkOut } = useAttendanceMutations();

  const log = data?.log ?? null;
  const onBreak = !!data?.activeBreak;
  const checkedIn = !!log?.check_in;
  const checkedOut = !!log?.check_out;
  const status = checkedOut ? 'Checked Out' : checkedIn ? 'Checked In' : 'Not Checked In';
  const hoursToday = log?.hours ?? 0;
  const busy = checkIn.isPending || checkOut.isPending;

  const handleToggle = () => {
    if (!checkedIn) {
      checkIn.mutate(undefined, {
        onSuccess: (d) => toast.success(`Checked in at ${d.time}`),
        onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to check in.'),
      });
    } else if (!checkedOut) {
      checkOut.mutate(undefined, {
        onSuccess: (d) => toast.success(`Checked out at ${d.time} · ${d.hours}h`),
        onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to check out.'),
      });
    }
  };

  return (
    <WidgetContainer
      config={timeSummaryConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => { refetch(); }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between rounded-card-sm border border-outline bg-surface-variant p-4">
          <div className="flex flex-col gap-1">
            <span className="text-body-sm font-medium text-on-surface-variant">Current Status</span>
            <span
              className={
                checkedIn && !checkedOut
                  ? 'w-fit rounded border border-success/30 bg-success/15 px-2 py-1 text-label-caps uppercase text-success'
                  : 'w-fit rounded border border-outline bg-surface px-2 py-1 text-label-caps uppercase text-on-surface'
              }
            >
              {status}
            </span>
            {onBreak && (
              <span className="mt-1 text-body-sm text-warning">On break ({data?.activeBreak?.break_type})</span>
            )}
          </div>
          <div className="flex flex-col gap-1 text-right">
            <span className="text-body-sm font-medium text-on-surface-variant">Hours Today</span>
            <span className="text-headline-md font-semibold tabular-nums text-primary">
              {hoursToday > 0 ? `${hoursToday}h` : '—'}
            </span>
          </div>
        </div>

        {checkedOut ? (
          <p className="text-center text-body-sm text-on-surface-variant">You're done for today.</p>
        ) : (
          <Button
            variant={checkedIn ? 'danger' : 'primary'}
            className="w-full font-bold"
            onClick={handleToggle}
            disabled={busy || (checkedIn && onBreak)}
          >
            {busy ? 'Updating…' : checkedIn ? 'Check Out' : 'Check In'}
          </Button>
        )}
      </div>
    </WidgetContainer>
  );
}
